/**
 * PropertyDetailSections — v2.89 (iter_130)
 * ==========================================
 *
 * Schema-driven property-detail section renderer. Reads the SAME central
 * schema as UnifiedSubmissionForm's ReviewPreview — form + detail page
 * cannot drift. Renders 9 sections in the canonical order:
 *
 *   1. Basic Details          6. Location
 *   2. Rooms & Area           7. Description
 *   3. Floor / Building       8. Documents
 *   4. Features & Amenities   9. Contact
 *   5. Price & Charges        (10. Admin Information — admin-only)
 *
 * Visibility rules
 * ----------------
 *  - PUBLIC: hide any empty optional field, hide the whole section
 *    if every field is empty.
 *  - ADMIN (`fullView`): show "Not provided" for empty important fields
 *    so operators can spot gaps.
 *  - Floor and Parking are ALWAYS rendered as separate rows — never
 *    concatenated (per user rule).
 *  - Furnishing is NEVER shown for Plot.
 *  - BHK is NEVER shown for Plot / Shop / Office / Building (per policy).
 */
import React from "react";
import {
  FIELD_META, readFieldValue, sectionsForType, getPropertyTypeKey,
  resolveParkingSummary,
} from "../shared/config/propertyFormSchema";
import { formatSize } from "../shared/services/api";

// Non-schema per-type render order for the Basic Details header row on
// the detail page. Deliberately does NOT include description/documents/
// contact (those are their own sections).
const CURRENCY_KEYS = new Set(["price", "monthly_rent", "monthly_lease",
                                "security_deposit", "maintenance"]);

// v2.91 — carpet_area removed from FIELDS_BY_TYPE. Keep set trimmed to
// what's currently in the catalogue so the formatter never has to guess.
const AREA_KEYS = new Set(["built_up_area", "plot_area"]);

function fmtINR(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return null;
  return "₹" + num.toLocaleString("en-IN");
}

function fmtValue(fieldId, raw, doc) {
  const meta = FIELD_META[fieldId];
  if (!meta) return null;
  if (raw === undefined || raw === null || raw === "") return null;
  if (meta.ui === "boolean") return raw ? "Yes" : null;
  if (meta.ui === "multiselect") {
    return Array.isArray(raw) && raw.length ? raw.join(", ") : null;
  }
  if (CURRENCY_KEYS.has(fieldId) || meta.ui === "money") return fmtINR(raw);
  if (AREA_KEYS.has(fieldId) || meta.ui === "area" || meta.ui === "area-top") {
    // v2.91 — Never hardcode "sqft". Built-up Area stores the value in
    // whatever unit the user selected (top.size_unit). Reuse the shared
    // formatSize helper so Hero + Details agree.
    if (fieldId === "built_up_area") {
      return formatSize(raw, (doc && doc.size_unit) || "sqft");
    }
    if (fieldId === "plot_area") return `${raw} sqyd`;
    return String(raw);
  }
  return String(raw);
}

/** Render one Key/Value row. */
function Row({ label, value, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-dashed border-[#F4F4F5] last:border-b-0"
         data-testid={testid}>
      <span className="text-[12px] text-zinc-500 font-medium">{label}</span>
      <span className="text-[13px] font-semibold text-zinc-800 text-right break-words min-w-0">
        {value}
      </span>
    </div>
  );
}

/**
 * Section renderer.
 *
 * v2.91/v2.93 responsibilities:
 *   • Skip any field whose meta says `showInDetails === false` (e.g. bhk,
 *     built_up_area, furnishing — already surfaced in Hero).
 *   • Render exactly ONE parking row per property. `parking` is the
 *     canonical schema field; legacy `car_parking` / `bike_parking` /
 *     `parking_type` are resolved into the same row via
 *     `resolveParkingSummary`. If the resolver returns mode=empty the
 *     entire parking row is hidden from the public detail page.
 *   • Preserve public-hides-empty behaviour; admin still sees "Not
 *     provided" for missing important fields.
 */
function Section({ id, label, fields, doc, fullView }) {
  const parking = resolveParkingSummary(doc);

  const rows = fields.map((fid) => {
    const meta = FIELD_META[fid];
    if (!meta) return null;
    // v2.91 — schema-declared hide from public detail.
    if (meta.showInDetails === false) return null;

    // v2.93 — Parking display: single canonical row, resolver-driven.
    // Reads canonical `custom_fields.parking` first, falls back to a
    // derived value from `car_parking` + `bike_parking`. `parking_type`
    // is deliberately never surfaced as its own row.
    if (fid === "parking") {
      const value = parking.value || "";
      if (!value) {
        if (!fullView) return null;
        return { fid, label: meta.label, value: <span className="text-zinc-400 italic font-normal">Not provided</span>, missing: true };
      }
      return { fid, label: meta.label, value, missing: false };
    }

    const raw = readFieldValue(fid, doc);
    const display = fmtValue(fid, raw, doc);
    if (display === null) {
      if (!fullView) return null; // hide empty in public
      return { fid, label: meta.label, value: <span className="text-zinc-400 italic font-normal">Not provided</span>, missing: true };
    }
    return { fid, label: meta.label, value: display, missing: false };
  }).filter(Boolean);
  if (!rows.length) return null;
  return (
    <section
      className="bg-white border border-[#E4E4E7] rounded-xl overflow-hidden"
      data-testid={`detail-section-${id}`}
    >
      <div className="bg-[#FAFAFA] px-4 py-2.5 border-b border-[#E4E4E7]">
        <h3 className="text-[13px] font-heading font-bold tracking-tight text-zinc-800">
          {label}
        </h3>
      </div>
      <div className="p-4">
        {rows.map((r) => (
          <Row key={r.fid} label={r.label} value={r.value}
               testid={`detail-row-${r.fid}${r.missing ? '-missing' : ''}`} />
        ))}
      </div>
    </section>
  );
}

/**
 * Main component — takes a property doc (as returned by the API) and
 * renders the 9 (or 10 for admins) canonical sections.
 *
 * Props
 * -----
 *  - property: the property document from /api/properties/{id}
 *  - fullView: boolean — set true for admin/team so missing fields
 *              render as "Not provided" instead of being hidden.
 *  - hideSections: optional Set<string> — section IDs to hide (used by
 *              PropertyDetail to skip Location / Description / Documents
 *              / Contact when they're already rendered by legacy blocks).
 */
export default function PropertyDetailSections({ property, fullView = false, hideSections = new Set() }) {
  if (!property || !property.property_type) return null;
  const ptKey = getPropertyTypeKey(property.property_type);
  if (!ptKey) {
    // Unknown property type — still show what we can under Basic Details.
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-[13px] text-amber-800"
           data-testid="detail-unknown-type">
        Property type &quot;{property.property_type}&quot; is not in the active catalog. Please contact admin.
      </div>
    );
  }

  const sections = sectionsForType(property.property_type);

  return (
    <div className="space-y-3" data-testid="property-detail-sections">
      {sections
        .filter((sec) => !hideSections.has(sec.id))
        .map((sec) => (
          <Section
            key={sec.id}
            id={sec.id}
            label={sec.label}
            fields={sec.fields}
            doc={property}
            fullView={fullView}
          />
        ))}
    </div>
  );
}
